import { Session } from '../hooks/useClassSessions';

export const POST_AUTH_RETURN_PATH_KEY = 'postAuthReturnPath';
export const POST_AUTH_SELECTED_SESSION_KEY = 'postAuthSelectedSession';

const isSafeReturnPath = (path: string | null): path is string =>
  Boolean(path && path.startsWith('/') && !path.startsWith('//'));

export const rememberPostAuthReturn = (
  selectedSession?: Session | null,
  explicitPath?: string
): void => {
  try {
    sessionStorage.setItem(
      POST_AUTH_RETURN_PATH_KEY,
      explicitPath ?? `${window.location.pathname}${window.location.search}`
    );
    if (selectedSession) {
      sessionStorage.setItem(POST_AUTH_SELECTED_SESSION_KEY, JSON.stringify(selectedSession));
    }
  } catch {
    // sessionStorage may be unavailable (private mode / blocked storage)
  }
};

/**
 * Store a post-auth return destination only if one has not already been saved.
 * Preserves an origin captured earlier (e.g. the calendar URL saved on the way
 * to /Register) rather than overwriting it with the current page.
 */
export const rememberPostAuthReturnIfAbsent = (
  selectedSession?: Session | null,
  explicitPath?: string
): void => {
  try {
    if (sessionStorage.getItem(POST_AUTH_RETURN_PATH_KEY)) {
      return;
    }
  } catch {
    // sessionStorage may be unavailable; fall through to the best-effort write
  }
  rememberPostAuthReturn(selectedSession, explicitPath);
};

/**
 * Read the stored post-auth return destination without clearing it.
 * Used by the VerifyCode back button, which should not consume the value that
 * the confirmation screen still needs after a successful verification.
 */
export const peekPostAuthReturn = (): {
  path: string | null;
  selectedSession: Session | undefined;
} => {
  try {
    const rawPath = sessionStorage.getItem(POST_AUTH_RETURN_PATH_KEY);
    const rawSession = sessionStorage.getItem(POST_AUTH_SELECTED_SESSION_KEY);

    let selectedSession: Session | undefined;
    if (rawSession) {
      try {
        selectedSession = JSON.parse(rawSession) as Session;
      } catch {
        selectedSession = undefined;
      }
    }

    return {
      path: isSafeReturnPath(rawPath) ? rawPath : null,
      selectedSession,
    };
  } catch {
    return { path: null, selectedSession: undefined };
  }
};

export const consumePostAuthReturn = (): {
  path: string | null;
  selectedSession: Session | undefined;
} => {
  try {
    const rawPath = sessionStorage.getItem(POST_AUTH_RETURN_PATH_KEY);
    const rawSession = sessionStorage.getItem(POST_AUTH_SELECTED_SESSION_KEY);
    sessionStorage.removeItem(POST_AUTH_RETURN_PATH_KEY);
    sessionStorage.removeItem(POST_AUTH_SELECTED_SESSION_KEY);

    let selectedSession: Session | undefined;
    if (rawSession) {
      try {
        selectedSession = JSON.parse(rawSession) as Session;
      } catch {
        selectedSession = undefined;
      }
    }

    return {
      path: isSafeReturnPath(rawPath) ? rawPath : null,
      selectedSession,
    };
  } catch {
    return { path: null, selectedSession: undefined };
  }
};
